import { Maatregel, OmleidingMaatregel, RijdtGroepMaatregel, StopMaatregel } from "@/entities/vsm";
import { gewijzigdMarkering } from "@/features/vsm/components/versperringsmaatregel/gewijzigd-markering.ts";
import { useMaatregelContext } from "@/features/vsm/maatregel-context";
import { useMaatregelVergelijking } from "@/features/vsm/vsm-vergelijking-context";
import { cn } from "@/shared/utils";
import { AanvullendeMaatregelForm, RijdtMaatregelForm, RijdtNietMaatregelForm } from "../edit-maatregel";
import { MaatregelRow, TreinserieLijst } from "./maatregel-row";
import { RijdtGroepBewerken } from "./rijdt-groep-bewerken";
import { StationCode, StationCodes, TrajectIndicator, VertrekTijd } from "./station-code";

type VersperringsmaatregelProps = {
    maatregel: Maatregel;
};

export function Versperringsmaatregel({ maatregel }: Readonly<VersperringsmaatregelProps>) {
    const context = useMaatregelContext();

    if (Maatregel.isInBewerking(maatregel)) {
        if (maatregel.type === "RijdtGroep") {
            return <RijdtGroepBewerken maatregel={maatregel} />;
        }

        if (context.variant === "nieuw") {
            return (
                <MaatregelForm
                    maatregel={maatregel}
                    intent={context.intent}
                    onSubmit={context.save}
                    onCancel={() => context.cancel(maatregel)}
                />
            );
        }
    }

    switch (maatregel.type) {
        case "Rijdt":
            return (
                <MaatregelRow maatregel={maatregel} iconName="train">
                    <TrajectIndicator maatregel={maatregel} />
                </MaatregelRow>
            );
        case "RijdtGroep":
            return <MaatregelRow maatregel={maatregel} iconName="train" content={<TreinserieLijst maatregel={maatregel} />} />;
        case "RijdtNiet":
            return (
                <MaatregelRow maatregel={maatregel} iconName="train-conflict-1">
                    <TrajectIndicator maatregel={maatregel} />
                </MaatregelRow>
            );
        case "Omleiding":
            return <OmleidingRow maatregel={maatregel} />;
        case "StopVerwijderen":
        case "ExtraStop":
            return <StopRow maatregel={maatregel} />;
    }
}

type MaatregelFormProps = {
    maatregel: Exclude<Maatregel, RijdtGroepMaatregel>;
    intent: "create" | "update";
    onSubmit: (maatregel: Maatregel) => void;
    onCancel: () => void;
};

function MaatregelForm({ maatregel, intent, onSubmit, onCancel }: Readonly<MaatregelFormProps>) {
    const formProps = { type: intent, onSubmit, onCancel } as const;

    switch (maatregel.type) {
        case "Rijdt":
            return <RijdtMaatregelForm {...formProps} initialData={maatregel} />;
        case "RijdtNiet":
            return <RijdtNietMaatregelForm {...formProps} initialData={maatregel} />;
        default:
            return <AanvullendeMaatregelForm {...formProps} initialData={maatregel} />;
    }
}

function OmleidingRow({ maatregel }: Readonly<{ maatregel: OmleidingMaatregel }>) {
    const { gewijzigdeVelden } = useMaatregelVergelijking(maatregel);

    return (
        <MaatregelRow
            maatregel={maatregel}
            iconName="train"
            extraRegel={
                <p className="text-s text-content-subtle group-data-[variant=nieuw]:text-content-default group-data-[variant=view]:text-content-default">
                    <span className={cn("font-bold", gewijzigdMarkering(gewijzigdeVelden.includes("type")))}>Omgeleid via </span>
                    <StationCodes codes={maatregel.omleiding.viaStationCodes} isGewijzigd={gewijzigdeVelden.includes("omleiding")} />
                </p>
            }
        >
            <TrajectIndicator maatregel={maatregel} />
        </MaatregelRow>
    );
}

function StopRow({ maatregel }: Readonly<{ maatregel: StopMaatregel }>) {
    const { gewijzigdeVelden } = useMaatregelVergelijking(maatregel);

    return (
        <MaatregelRow maatregel={maatregel} iconName="train">
            <p className="text-s group-data-[variant=nieuw]:text-content-default group-data-[variant=view]:text-content-default">
                <VertrekTijd vertrekTijd={maatregel.vertrekTijd} isGewijzigd={gewijzigdeVelden.includes("vertrekTijd")} />
                {maatregel.type === "StopVerwijderen" && (
                    <span className={cn("font-bold", gewijzigdMarkering(gewijzigdeVelden.includes("type")))}>Stopt niet in: </span>
                )}
                {maatregel.type === "ExtraStop" && (
                    <span className={cn("font-bold", gewijzigdMarkering(gewijzigdeVelden.includes("type")))}>Extra stop in: </span>
                )}
                <StationCode code={maatregel.vanStationCode} isGewijzigd={gewijzigdeVelden.includes("vanStationCode")} />
                {maatregel.naarStationCode && (
                    <span>
                        {" "}
                        ri. <StationCode code={maatregel.naarStationCode} isGewijzigd={gewijzigdeVelden.includes("naarStationCode")} />
                    </span>
                )}
            </p>
        </MaatregelRow>
    );
}
